import { type Action, can, newId } from '@quickreport/domain';
import {
  AuthError,
  kvDelete,
  kvGet,
  kvSet,
  OfflineError,
  SyncEngine,
  type SyncStatus,
} from '@quickreport/sync';
import { topUpNumbers } from '@quickreport/workspace';
import {
  createContext,
  type ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  useSyncExternalStore,
} from 'react';
import { ApiRequestError, api, createRemote } from '../lib/api';
import { desktop } from '../lib/desktop';
import { type LocalDb, openLocalDb, wipeLocalDb } from '../local-db/client';
import { checkPin, clearPin, hasPin, type PinResult, setPin as storePin } from '../local-db/pin';
import { firstSite, seedStarterTemplates } from '../local-db/workspace';

export interface AuthState {
  userId: string;
  userName: string;
  email: string | null;
  phone: string | null;
  orgId: string;
  orgName: string;
  memberId: string;
  roles: string[];
  siteId: string | null;
  deviceId: string | null;
  /** Set in a support session: the person from LabView who opened this lab as this user. */
  support?: { by: string };
  /** Where patients open reports; printed in the QR code. */
  verifyBaseUrl: string;
}

export interface Blocked {
  kind: 'account' | 'lab';
  message: string;
}

interface Me {
  user: { id: string; name: string; email: string | null; phone: string | null };
  memberships: {
    memberId: string;
    orgId: string;
    orgName: string;
    siteId: string | null;
    displayName: string;
    roles: string[];
  }[];
  currentOrgId: string | null;
  deviceId: string | null;
  config: { verifyBaseUrl: string };
  support?: { by: string } | null;
  blocked: { kind: 'account' | 'lab'; code: string; message: string } | null;
}

export type Phase =
  | { kind: 'booting' }
  | { kind: 'need_internet' }
  | { kind: 'signed_out'; notice?: string }
  | { kind: 'need_org'; userName: string }
  /** The LabView team paused this person or their lab. */
  | { kind: 'blocked'; blocked: Blocked }
  | { kind: 'switch_user'; pending: number }
  | { kind: 'preparing'; message: string }
  | { kind: 'locked'; auth: AuthState; expired: boolean }
  /** `expired`: the server no longer accepts this sign-in, but work carries on and waits to sync. */
  | { kind: 'ready'; auth: AuthState; db: LocalDb; engine: SyncEngine; expired?: boolean }
  | { kind: 'fatal'; message: string };

interface Actions {
  requestCode(identifier: string): Promise<{
    challengeId: string;
    maskedTo: string;
    resendAfterSeconds: number;
    devOtp?: string;
  }>;
  /** `remember`: stay signed in on this computer, rather than until the browser is closed. */
  verifyCode(challengeId: string, otp: string, remember?: boolean): Promise<void>;
  /** Sign in with an email address or mobile number and a password instead of a code. */
  signInWithPassword(identifier: string, password: string, remember?: boolean): Promise<void>;
  createLab(input: {
    yourName: string;
    /** Optional: lets the person sign in without a code next time. */
    password?: string;
    labName: string;
    siteName: string;
    workflowLevel: 'simple' | 'standard' | 'nabl';
  }): Promise<void>;
  /** Sign out. If changes have not been uploaded, returns how many and does nothing unless forced. */
  signOut(force?: boolean): Promise<{ blocked: number } | null>;
  wipeAndRestart(): Promise<void>;
  retry(): void;
  /** Whether a PIN is set on this computer. */
  pinSet: boolean;
  setPin(pin: string): Promise<void>;
  removePin(): Promise<void>;
  /** Lock the screen. The PIN opens it again, with or without internet. */
  lock(): Promise<void>;
  unlock(pin: string): Promise<PinResult>;
  /** Used by the background check when the LabView team pauses access while the app is open. */
  blockNow(blocked: Blocked): void;
  /** Sign in with a code again, keeping the saved work on this computer. */
  reauthenticate(opts?: { forgotPin?: boolean }): void;
}

const AuthContext = createContext<{ phase: Phase } & Actions>(null as never);

export function useAuth() {
  return useContext(AuthContext);
}

function deviceName(): string {
  const ua = navigator.userAgent;
  const browser = /Edg\//.test(ua)
    ? 'Edge'
    : /Chrome\//.test(ua)
      ? 'Chrome'
      : /Firefox\//.test(ua)
        ? 'Firefox'
        : /Safari\//.test(ua)
          ? 'Safari'
          : 'Browser';
  const os = /Windows/.test(ua)
    ? 'Windows'
    : /Mac OS/.test(ua)
      ? 'Mac'
      : /Android/.test(ua)
        ? 'Android'
        : /Linux/.test(ua)
          ? 'Linux'
          : 'computer';
  return `${browser} on ${os}`;
}

async function readAuth(db: LocalDb): Promise<AuthState | null> {
  const raw = await kvGet(db, 'auth');
  return raw ? (JSON.parse(raw) as AuthState) : null;
}

/** Keep this computer stocked with lab numbers so it can number reports offline. */
async function requestNumbers(db: LocalDb, orgId: string, siteId: string) {
  try {
    await topUpNumbers(db, orgId, siteId, (sid, count) =>
      api('POST', '/v1/sync/number-blocks', { siteId: sid, count }),
    );
  } catch (err) {
    console.warn('[numbers] could not reserve lab numbers', err);
  }
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [phase, setPhase] = useState<Phase>({ kind: 'booting' });
  const engineRef = useRef<SyncEngine | null>(null);
  const dbRef = useRef<LocalDb | null>(null);
  const [pinSet, setPinSet] = useState(false);
  /** True once the PIN has been entered since the app opened or was locked. */
  const unlockedRef = useRef(false);
  /** Set when the person chose "sign in with a code instead" because they forgot the PIN. */
  const forgotRef = useRef(false);

  /** First sync, device registration, then hand over to the app. */
  const prepare = useCallback(
    async (db: LocalDb, auth: AuthState, offline: boolean, expired = false) => {
      setPhase({ kind: 'preparing', message: 'Setting up this computer…' });
      const engine = new SyncEngine({ db, remote: createRemote(), orgId: auth.orgId });
      engineRef.current = engine;
      const firstTime = (await kvGet(db, 'pull_rev', auth.orgId)) === null;
      if (firstTime && offline) return setPhase({ kind: 'need_internet' });
      if (firstTime) {
        await engine.syncNow();
        const st = engine.getStatus();
        if (st.state === 'signed_out')
          return setPhase({ kind: 'signed_out', notice: 'Your session ended. Sign in again.' });
        if (st.state !== 'idle')
          return setPhase({
            kind: 'fatal',
            message:
              st.error ??
              'Could not download your lab’s data. Check your connection and try again.',
          });
      }

      const site = await firstSite(db, auth.orgId);
      let next = { ...auth, siteId: site?.id ?? auth.siteId };

      if ((await kvGet(db, 'seed_pending', auth.orgId)) === '1') {
        await seedStarterTemplates(db, auth.orgId);
        await kvDelete(db, 'seed_pending', auth.orgId);
      }

      if (!offline && next.siteId) {
        try {
          const known = next.deviceId ?? (await kvGet(db, 'device_id', auth.orgId));
          let ok = false;
          if (known) {
            try {
              await api('POST', '/v1/session/device', { deviceId: known });
              next = { ...next, deviceId: known };
              ok = true;
            } catch (err) {
              if (!(err instanceof ApiRequestError && err.status === 404)) throw err;
            }
          }
          if (!ok) {
            const d = await api<{ deviceId: string }>('POST', '/v1/devices', {
              siteId: next.siteId,
              name: deviceName(),
              platform: 'web',
            });
            await kvSet(db, 'device_id', d.deviceId, auth.orgId);
            next = { ...next, deviceId: d.deviceId };
          }
        } catch (err) {
          if (err instanceof ApiRequestError && err.code === 'device_revoked') {
            await wipeLocalDb();
            return setPhase({
              kind: 'signed_out',
              notice: 'This computer was removed from the lab.',
            });
          }
          // Registering can wait until the next start; the app still works.
          console.warn('[auth] device registration failed', err);
        }
      }
      await kvSet(db, 'auth', JSON.stringify(next));
      if (!offline && next.siteId) await requestNumbers(db, next.orgId, next.siteId);
      if (!expired) engine.start();
      setPhase({ kind: 'ready', auth: next, db, engine, expired });
    },
    [],
  );

  const doBoot = useCallback(async () => {
    setPhase({ kind: 'booting' });
    try {
      const db = await openLocalDb();
      dbRef.current = db;
      engineRef.current?.stop();
      engineRef.current = null;
      const saved = await readAuth(db);
      const pin = saved ? await hasPin(db) : false;
      setPinSet(pin);
      const wasLocked = pin && (await kvGet(db, 'locked')) === '1';
      const askPin = (extra: boolean) => pin && !unlockedRef.current && (extra || wasLocked);

      let me: Me;
      try {
        me = await api<Me>('GET', '/v1/me');
      } catch (err) {
        if (err instanceof OfflineError) {
          if (!saved) return setPhase({ kind: 'need_internet' });
          // No internet: the saved lab opens, behind the PIN if there is one.
          if (askPin(true)) return setPhase({ kind: 'locked', auth: saved, expired: false });
          return prepare(db, saved, true);
        }
        if (err instanceof ApiRequestError && err.status === 401) {
          if (err.code === 'device_revoked') {
            await wipeLocalDb();
            return setPhase({
              kind: 'signed_out',
              notice:
                'This computer was removed from the lab, so its data was cleared. Sign in again to set it up.',
            });
          }
          // The server no longer knows this sign-in. Saved work stays usable and waits to sync.
          if (saved && !forgotRef.current) {
            if (askPin(true)) return setPhase({ kind: 'locked', auth: saved, expired: true });
            return prepare(db, saved, true, true);
          }
          return setPhase({ kind: 'signed_out' });
        }
        throw err;
      }

      // Local data belongs to the person who set this device up.
      if (saved && saved.userId !== me.user.id) {
        const n = await db.query<{ n: number }>(
          'select count(*)::int n from (select 1 from outbox group by table_name, row_id) x',
        );
        return setPhase({ kind: 'switch_user', pending: n.rows[0]?.n ?? 0 });
      }

      // Forgot the PIN and proved who you are with a code: the PIN is cleared so a new one can be set.
      if (saved && forgotRef.current && saved.userId === me.user.id) {
        await clearPin(db);
        setPinSet(false);
        unlockedRef.current = true;
        forgotRef.current = false;
      } else if (askPin(false)) {
        return setPhase({ kind: 'locked', auth: saved as AuthState, expired: false });
      }

      if (me.blocked) {
        return setPhase({
          kind: 'blocked',
          blocked: { kind: me.blocked.kind, message: me.blocked.message },
        });
      }
      const orgs = me.memberships;
      if (orgs.length === 0) return setPhase({ kind: 'need_org', userName: me.user.name });
      const chosen =
        orgs.find((m) => m.orgId === me.currentOrgId) ??
        orgs.find((m) => m.orgId === saved?.orgId) ??
        orgs[0];
      if (!chosen) return setPhase({ kind: 'need_org', userName: me.user.name });
      if (chosen.orgId !== me.currentOrgId)
        await api('POST', '/v1/session/org', { orgId: chosen.orgId });

      const roles = [
        ...new Set(orgs.filter((m) => m.orgId === chosen.orgId).flatMap((m) => m.roles)),
      ];
      const auth: AuthState = {
        userId: me.user.id,
        userName: chosen.displayName || me.user.name,
        email: me.user.email,
        phone: me.user.phone,
        orgId: chosen.orgId,
        orgName: chosen.orgName,
        memberId: chosen.memberId,
        roles,
        siteId: saved?.orgId === chosen.orgId ? saved.siteId : null,
        deviceId: saved?.orgId === chosen.orgId ? saved.deviceId : null,
        verifyBaseUrl: me.config.verifyBaseUrl,
        ...(me.support ? { support: me.support } : {}),
      };
      await kvSet(db, 'auth', JSON.stringify(auth));
      await prepare(db, auth, false);
    } catch (err) {
      setPhase({ kind: 'fatal', message: err instanceof Error ? err.message : String(err) });
    }
  }, [prepare]);

  const bootQueue = useRef<Promise<void>>(Promise.resolve());
  const boot = useCallback(() => {
    // One start-up at a time: overlapping ones (React's development double-mount, or a sign-in
    // finishing while a start-up is running) would each start a sync engine on the same data.
    const run = bootQueue.current.then(() => doBoot());
    bootQueue.current = run.catch(() => undefined);
    return run;
  }, [doBoot]);

  useEffect(() => {
    void boot();
    return () => engineRef.current?.stop();
  }, [boot]);

  // If the server ends the session or removes this device while the app is open, say so.
  useEffect(() => {
    if (phase.kind !== 'ready') return;
    let lastTopUp = 0;
    return phase.engine.subscribe((s) => {
      if (s.state === 'idle' && phase.auth.siteId && Date.now() - lastTopUp > 60_000) {
        lastTopUp = Date.now();
        void requestNumbers(phase.db, phase.auth.orgId, phase.auth.siteId);
      }
      if (s.state !== 'signed_out') return;
      phase.engine.stop();
      if (s.authReason === 'revoked') {
        void wipeLocalDb().then(() =>
          setPhase({
            kind: 'signed_out',
            notice: 'This computer was removed from the lab, so its data was cleared.',
          }),
        );
      } else {
        // Keep working; the banner offers to sign in again so changes can sync.
        setPhase((p) => (p.kind === 'ready' ? { ...p, expired: true } : p));
      }
    });
  }, [phase]);

  const actions = useMemo<Actions>(
    () => ({
      requestCode: (identifier) => api('POST', '/v1/auth/otp/request', { identifier }),
      async verifyCode(challengeId, otp, remember = true) {
        const d = desktop();
        const res = await api<{ token?: string }>('POST', '/v1/auth/otp/verify', {
          challengeId,
          otp,
          client: d ? 'desktop' : 'web',
          remember,
        });
        if (d && res.token) {
          await d.vault.setToken(res.token);
          window.dispatchEvent(new CustomEvent('qr-token', { detail: res.token }));
        }
        await boot();
      },
      async signInWithPassword(identifier, password, remember = true) {
        const d = desktop();
        const res = await api<{ token?: string }>('POST', '/v1/auth/password/login', {
          identifier,
          password,
          client: d ? 'desktop' : 'web',
          remember,
        });
        if (d && res.token) {
          await d.vault.setToken(res.token);
          window.dispatchEvent(new CustomEvent('qr-token', { detail: res.token }));
        }
        await boot();
      },
      async createLab(input) {
        const db = dbRef.current;
        if (!db) throw new Error('The local database is not open');
        const orgId = newId();
        if (input.yourName.trim()) await api('PATCH', '/v1/me', { name: input.yourName.trim() });
        if (input.password) {
          // A password is a convenience: the lab is still made if it cannot be saved now.
          await api('POST', '/v1/me/password', { password: input.password }).catch(() => undefined);
        }
        await api('POST', '/v1/orgs', {
          orgId,
          name: input.labName.trim(),
          site: {
            id: newId(),
            name: input.siteName.trim(),
            workflowLevel: input.workflowLevel,
          },
        });
        await kvSet(db, 'seed_pending', '1', orgId);
        await boot();
      },
      async signOut(force = false) {
        const db = dbRef.current;
        if (db && !force) {
          const n = await db.query<{ n: number }>(
            'select count(*)::int n from (select 1 from outbox group by table_name, row_id) x',
          );
          const pending = n.rows[0]?.n ?? 0;
          if (pending > 0) return { blocked: pending };
        }
        engineRef.current?.stop();
        await api('POST', '/v1/auth/logout').catch(() => undefined);
        await desktop()?.vault.clearToken();
        await wipeLocalDb();
        window.location.assign('/');
        return null;
      },
      async wipeAndRestart() {
        engineRef.current?.stop();
        await api('POST', '/v1/auth/logout').catch(() => undefined);
        await desktop()?.vault.clearToken();
        await wipeLocalDb();
        window.location.assign('/');
      },
      retry: () => void boot(),
      pinSet,
      async setPin(pin) {
        const db = dbRef.current;
        if (!db) throw new Error('The local database is not open');
        await storePin(db, pin);
        unlockedRef.current = true;
        setPinSet(true);
      },
      async removePin() {
        const db = dbRef.current;
        if (!db) return;
        await clearPin(db);
        await kvDelete(db, 'locked');
        setPinSet(false);
      },
      async lock() {
        const db = dbRef.current;
        const saved = db ? await readAuth(db) : null;
        if (!db || !saved || !(await hasPin(db))) return;
        engineRef.current?.stop();
        await kvSet(db, 'locked', '1');
        unlockedRef.current = false;
        setPhase((p) => ({
          kind: 'locked',
          auth: saved,
          expired: p.kind === 'ready' ? Boolean(p.expired) : false,
        }));
      },
      async unlock(pin) {
        const db = dbRef.current;
        if (!db) return { ok: false, reason: 'wrong', triesLeft: 0 };
        const r = await checkPin(db, pin);
        if (r.ok) {
          await kvDelete(db, 'locked');
          unlockedRef.current = true;
          await boot();
        }
        return r;
      },
      blockNow(blocked) {
        engineRef.current?.stop();
        setPhase({ kind: 'blocked', blocked });
      },
      reauthenticate(opts) {
        forgotRef.current = Boolean(opts?.forgotPin);
        engineRef.current?.stop();
        setPhase({
          kind: 'signed_out',
          notice: 'Sign in to sync. Your saved work stays on this computer.',
        });
      },
    }),
    [boot, pinSet],
  );

  return <AuthContext.Provider value={{ phase, ...actions }}>{children}</AuthContext.Provider>;
}

const IDLE: SyncStatus = { state: 'idle', pending: 0, conflicts: 0, lastSyncedAt: null };

/** Live sync status for the header. */
export function useSyncStatus(engine: SyncEngine | null): SyncStatus {
  return useSyncExternalStore(
    (cb) => (engine ? engine.subscribe(cb) : () => undefined),
    () => engine?.getStatus() ?? IDLE,
  );
}

export function hasRole(auth: AuthState, action: Action): boolean {
  return can(auth.roles, action);
}

export { AuthError };
